'use client';

import { useContext, useEffect, useState, type FC } from 'react';
import {
  Button,
  ButtonGroup,
  Flex,
  Dialog,
  DraggableQuestionnaireItems,
  Input,
  Select,
  EntitySelectorInputChips,
  Heading,
  LargeBulkSendConfirmationDialog,
} from '@/_components';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { sendOpenItems } from '@/_api/admin-questionnaires';
import { TAX_YEARS_WITHOUT_DEFAULT } from '@/_constants/year-options';
import { getIsPastDate, getNDaysAfter } from '@/_utils/dates';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { useEntityCount } from '@/_hooks/useEntityCount';
import { useItemList } from '@/_hooks/useItemList';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { processMessageText } from '@/_utils/strings';

const TODAYS_DATE: string = getNDaysAfter(0);
const AFTER_14_DAYS: string = getNDaysAfter(14);

export const SendCustomQuestionnaireDialog: FC<{
  initiallySelectedCompanyIds?: Array<number>;
  isOpen: boolean;
  onClose: () => void;
}> = ({ initiallySelectedCompanyIds, isOpen, onClose }) => {
  const { selectedCompany } = useContext(CompanyContext);
  const { loadQuestionnaires } = useContext(QuestionnaireContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [taxYear, setTaxYear] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [openItemsRegarding, setOpenItemsRegarding] = useState<string>('');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Array<number>>(
    (selectedCompany && [selectedCompany.id]) ||
      initiallySelectedCompanyIds ||
      [],
  );

  const {
    items,
    isEmpty,
    addItem,
    removeItem,
    updateItem,
    reorderItems,
    resetItems,
    getItems,
  } = useItemList();

  const [
    shouldShowLargeBulkSendConfirmation,
    setShouldShowLargeBulkSendConfirmation,
  ] = useState<boolean>(false);
  const [shouldSendDraft, setShouldSendDraft] = useState<boolean>(false);
  const [bulkSelectedEntityTypes, setBulkSelectedEntityTypes] = useState<
    Array<string>
  >([]);

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const entityCountForConfirmation = useEntityCount({
    bulkSelectedEntityTypes,
    selectedCompanyIds,
  });

  const onChipsChange = (chips: Array<number>) => setSelectedCompanyIds(chips);

  const isDueDateValid: boolean = !!dueDate
    ? getIsPastDate(TODAYS_DATE, dueDate)
    : true;

  const isValid: boolean =
    isDueDateValid &&
    (!!selectedCompanyIds?.length || !!bulkSelectedEntityTypes?.length) &&
    !!taxYear &&
    !!openItemsRegarding &&
    !isEmpty &&
    !!items.every((item) => item.text);

  useEffect(() => {
    if (initiallySelectedCompanyIds && initiallySelectedCompanyIds.length) {
      setSelectedCompanyIds(initiallySelectedCompanyIds);
    }
  }, [initiallySelectedCompanyIds]);

  // Preselect the selected company
  useEffect(() => {
    if (selectedCompany?.id) {
      onChipsChange([selectedCompany?.id]);
    }
  }, [selectedCompany]);

  const onReset = () => {
    resetItems();
    setOpenItemsRegarding('');
    setDueDate(AFTER_14_DAYS);
    setTaxYear('');
    setBulkSelectedEntityTypes([]);

    return onClose();
  };

  const onSubmit = (isDraft: boolean) => {
    setShouldSendDraft(isDraft);

    if (selectedCompanyIds.length >= 10 || !!bulkSelectedEntityTypes?.length) {
      setShouldShowLargeBulkSendConfirmation(true);
    } else {
      performQuestionnaireSend(isDraft);
    }
  };

  const performQuestionnaireSend = async (isDraft: boolean) => {
    setIsLoading(true);

    const questionsToCreate = getItems().map((item) => {
      const isUpload = item.question_type === 'upload';

      // Sanitize text to remove non-breaking spaces from QuillJS bug (temp patch for https://github.com/slab/quill/issues/4535)
      // Also watch: https://github.com/slab/quill/issues/4509
      const sanitizedText = processMessageText(item.text);

      return {
        options: isUpload ? [sanitizedText] : [],
        question_type: item.question_type,
        text: isUpload ? `Please upload ${sanitizedText}` : sanitizedText,
      };
    });

    const params = {
      company_ids: selectedCompanyIds,
      company_types: bulkSelectedEntityTypes,
      due_date: dueDate,
      is_draft: isDraft,
      name: openItemsRegarding,
      questions_to_create: questionsToCreate,
      tax_year: taxYear,
    };

    try {
      await sendOpenItems({ params, token });
      addToast('success', 'Open item sent');
      setTimeout(async () => {
        await loadQuestionnaires();
      }, 3000);
      onReset();
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Create Custom Questionnaire"
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={6}>
            <Flex gap={6}>
              <EntitySelectorInputChips
                label="Select Entity"
                isBulkTypeSelectionEnabled={!selectedCompany?.id}
                setBulkSelectedEntityTypes={setBulkSelectedEntityTypes}
                bulkSelectedEntityTypes={bulkSelectedEntityTypes}
                onClearBulkSelection={() => setBulkSelectedEntityTypes([])}
                isDisabled={
                  !!selectedCompany?.id || !!bulkSelectedEntityTypes?.length
                }
                hideDisabledEntities={true}
                preselectedEntityIds={selectedCompanyIds}
                onChipsChange={onChipsChange}
              />
              <Input
                label="Open items regarding"
                type="text"
                isDisabled={isLoading}
                value={openItemsRegarding}
                onChange={(e) => setOpenItemsRegarding(e.target.value)}
                placeholder="Enter a subject or topic"
              />
            </Flex>

            <Flex gap={8} yAlign="end">
              <Select
                isDisabled={isLoading}
                items={TAX_YEARS_WITHOUT_DEFAULT}
                label="Tax Year"
                placeholder="Select tax year"
                onChange={(e) => setTaxYear(e.target.value)}
                selectedValue={taxYear}
              />

              <Input
                label="Due Date"
                min={getNDaysAfter(1)}
                type="date"
                isDisabled={isLoading}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </Flex>

            <Heading level={5}>Items</Heading>

            <DraggableQuestionnaireItems
              items={items}
              updateItem={updateItem}
              removeItem={removeItem}
              addItem={addItem}
              onReorder={reorderItems}
              isLoading={isLoading}
              addButtonText="Add item"
            />
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <Flex isBetween={true}>
            <ButtonGroup>
              <Button value="Cancel" onClick={onClose} intent="secondary" />
            </ButtonGroup>

            <ButtonGroup align="right">
              <Button
                value={
                  selectedCompanyIds?.length > 1 ? 'Save drafts' : 'Save draft'
                }
                intent="secondary"
                onClick={() => onSubmit(true)}
                type="button"
                isDisabled={isLoading || !isValid}
                isLoading={isLoading}
              />

              <Button
                value={
                  selectedCompanyIds?.length > 1
                    ? 'Send Open Items'
                    : 'Send Open Item'
                }
                intent="primary"
                onClick={() => onSubmit(false)}
                type="button"
                isDisabled={isLoading || !isValid}
                isLoading={isLoading}
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Footer>
      </Dialog>
      {shouldShowLargeBulkSendConfirmation && (
        <LargeBulkSendConfirmationDialog
          isOpen={shouldShowLargeBulkSendConfirmation}
          onClose={() => setShouldShowLargeBulkSendConfirmation(false)}
          bulkSelectedEntityTypes={bulkSelectedEntityTypes}
          entityCount={entityCountForConfirmation}
          onConfirm={() => {
            performQuestionnaireSend(shouldSendDraft);
            setShouldShowLargeBulkSendConfirmation(false);
          }}
          isDraft={shouldSendDraft}
        />
      )}
    </>
  );
};
